import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronDown, ArrowRight } from 'lucide-react';

// ── Easy on/off toggle ────────────────────────────────────────────────
export const INTRO_ENABLED = true;
const SESSION_KEY = 'ktc_intro_seen';

// ── Content beats (real brand copy + real facility/product photos) ────
const OVERLAY = 'linear-gradient(180deg, rgba(6,20,13,0.26) 0%, rgba(6,20,13,0.36) 55%, rgba(6,20,13,0.60) 100%)';
const scenes = [
  {
    kicker: 'Since 1999',
    title: 'Kassam Trading Company',
    sub: 'Est. 1999 · Karachi, Pakistan',
    image: 'https://images.pexels.com/photos/34221784/pexels-photo-34221784.jpeg?auto=compress&cs=tinysrgb&w=1920',
    glow: 'radial-gradient(60% 55% at 50% 45%, rgba(201,162,94,0.22), transparent 62%)',
  },
  {
    kicker: 'The origin',
    title: "Sown in Pakistan's richest paddy soil",
    sub: 'Long-grain Basmati 1121 and 1509. Aromatic, aged and naturally slender.',
    image: 'https://images.pexels.com/photos/2589457/pexels-photo-2589457.jpeg?auto=compress&cs=tinysrgb&w=1920',
    glow: 'radial-gradient(55% 55% at 30% 42%, rgba(201,162,94,0.20), transparent 60%)',
  },
  {
    kicker: 'Under one roof',
    title: 'Milled, graded and packed under one roof',
    sub: 'Our Port Qasim facility controls every step, from paddy to the sealed export bag.',
    image: '/mill-facility.jpg',
    glow: 'radial-gradient(55% 55% at 72% 46%, rgba(168,92,50,0.40), transparent 62%)',
    stats: [
      { n: '25+', l: 'Years' },
      { n: '30+', l: 'Countries' },
      { n: '7,000', l: 'MT / month' },
    ],
  },
  {
    kicker: 'Worldwide',
    title: 'Shipped FOB Port Qasim to over 30 countries',
    sub: "From Karachi's harbour to buyers across Africa, the Middle East, Asia and Europe.",
    image: 'https://images.pexels.com/photos/33587048/pexels-photo-33587048.jpeg?auto=compress&cs=tinysrgb&w=1920',
    glow: 'radial-gradient(62% 52% at 50% 55%, rgba(201,162,94,0.20), transparent 66%)',
  },
  {
    kicker: 'Welcome',
    title: 'Enter the harvest',
    sub: 'Premium rice, salt and grains, milled and exported from Pakistan.',
    image: 'https://images.pexels.com/photos/36346840/pexels-photo-36346840.jpeg?auto=compress&cs=tinysrgb&w=1920',
    glow: 'radial-gradient(60% 60% at 50% 50%, rgba(201,162,94,0.30), transparent 60%)',
    cta: true,
  },
];

// piecewise-linear interpolation with clamped ends
const seg = (p, pts) => {
  if (p <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i += 1) {
    if (p <= pts[i][0]) {
      const [x0, y0] = pts[i - 1];
      const [x1, y1] = pts[i];
      const t = (p - x0) / (x1 - x0);
      return y0 + (y1 - y0) * t;
    }
  }
  return pts[pts.length - 1][1];
};

const OP = [
  [[0, 1], [0.14, 1], [0.2, 0]],
  [[0.16, 0], [0.24, 1], [0.34, 1], [0.4, 0]],
  [[0.36, 0], [0.44, 1], [0.54, 1], [0.6, 0]],
  [[0.56, 0], [0.64, 1], [0.74, 1], [0.8, 0]],
  [[0.76, 0], [0.86, 1], [1, 1]],
];
const YY = [
  [[0, 0], [0.2, -40]],
  [[0.2, 40], [0.4, -40]],
  [[0.4, 40], [0.6, -40]],
  [[0.6, 40], [0.8, -40]],
  [[0.8, 40], [1, 0]],
];

// ── Signature motif: rice stalk (line + grain ovals) ──────────────────
const RiceStalk = ({ className, style }) => {
  const grains = [];
  for (let i = 0; i < 9; i += 1) {
    const y = 150 + i * 26;
    const off = 10 + (8 - i) * 3.2;
    grains.push(
      <g key={i}>
        <ellipse cx={100 - off} cy={y} rx="6.5" ry="16" transform={`rotate(-26 ${100 - off} ${y})`} />
        <ellipse cx={100 + off} cy={y - 6} rx="6.5" ry="16" transform={`rotate(26 ${100 + off} ${y - 6})`} />
      </g>,
    );
  }
  return (
    <svg viewBox="0 0 200 520" fill="none" className={className} style={style} aria-hidden>
      <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
        <path d="M100 515 C100 420 100 340 100 120" />
        {grains}
        <path d="M100 130 C100 108 108 96 122 90" />
        <path d="M100 130 C100 108 92 96 78 90" />
      </g>
    </svg>
  );
};

const GrainDots = ({ className }) => (
  <div className={className} aria-hidden>
    {Array.from({ length: 14 }).map((_, i) => (
      <span
        key={i}
        className="absolute rounded-full bg-ktc-goldLight/40"
        style={{ width: 3 + (i % 3), height: 3 + (i % 3), left: `${(i * 37) % 100}%`, top: `${(i * 53) % 100}%`, opacity: 0.25 + (i % 4) * 0.12 }}
      />
    ))}
  </div>
);

const CinematicIntro = ({ onFinish, preview = false }) => {
  const ref = useRef(null);
  const [reduced, setReduced] = useState(false);
  const [ready, setReady] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    if (!preview && sessionStorage.getItem(SESSION_KEY) === '1') setHidden(true);
    setReady(true);
  }, [preview]);

  const markSeen = useCallback(() => {
    if (preview) return;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* ignore */ }
  }, [preview]);

  // deterministic scroll progress across the intro section
  useEffect(() => {
    if (!ready || reduced || hidden) return undefined;
    let raf = 0;
    const setLock = (on) => document.documentElement.classList.toggle('intro-lock', on);
    const compute = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(window.scrollY - el.offsetTop, 0), total);
      const p = total > 0 ? scrolled / total : 0;
      setProgress(p);
      setActive(Math.min(scenes.length - 1, Math.max(0, Math.floor(p * scenes.length + 0.0001))));
      setLock(p < 0.992);
      if (p > 0.985) markSeen();
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(compute); };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
      setLock(false);
    };
  }, [ready, reduced, hidden, markSeen]);

  const releaseLock = () => document.documentElement.classList.remove('intro-lock');
  const scrollPast = () => {
    const el = ref.current;
    if (el) window.scrollTo({ top: el.offsetTop + el.offsetHeight + 2, behavior: 'smooth' });
  };
  const enter = () => { markSeen(); releaseLock(); requestAnimationFrame(scrollPast); if (onFinish) onFinish(); };

  const goTo = (i) => {
    const el = ref.current;
    if (!el) return;
    const range = el.offsetHeight - window.innerHeight;
    const p = (i + 0.5) / scenes.length;
    window.scrollTo({ top: el.offsetTop + p * range, behavior: 'smooth' });
  };

  if (!INTRO_ENABLED || !ready || hidden) return null;

  // ── Reduced motion: static stacked, no scroll-jacking ──────────────
  if (reduced) {
    return (
      <section data-testid="cinematic-intro" className="relative z-[60]">
        <button type="button" onClick={enter} data-testid="intro-skip" className="fixed top-4 right-4 z-[62] text-[11px] uppercase tracking-[0.2em] text-ktc-ivory bg-ktc-green/80 border border-ktc-ivory/20 rounded-full px-4 py-2">
          Skip intro →
        </button>
        {scenes.map((s, i) => (
          <div key={i} className="relative min-h-[70vh] flex flex-col items-center justify-center text-center px-6 py-16 text-ktc-ivory bg-[#0A1E13]">
            {s.image && <img src={s.image} alt="" aria-hidden loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-[0.92]" />}
            <div className="absolute inset-0" style={{ background: OVERLAY }} />
            <div className="absolute inset-0" style={{ background: s.glow }} />
            <div className="absolute inset-0 grain-texture opacity-[0.12]" aria-hidden />
            <div className="relative max-w-2xl">
              <span className="text-[12px] uppercase tracking-[0.28em] text-ktc-goldLight intro-legible">{s.kicker}</span>
              <h2 className="font-intro intro-legible text-3xl sm:text-5xl font-semibold leading-[1.05] mt-4">{s.title}</h2>
              <p className="text-ktc-ivory/80 intro-legible mt-4 text-base sm:text-lg">{s.sub}</p>
              {s.stats && (
                <div className="flex justify-center gap-8 mt-6">
                  {s.stats.map((st) => (
                    <div key={st.l}><div className="font-serif text-2xl text-ktc-goldLight">{st.n}</div><div className="text-xs uppercase tracking-wider text-ktc-ivory/60">{st.l}</div></div>
                  ))}
                </div>
              )}
              {s.cta && (
                <button type="button" onClick={enter} data-testid="intro-enter-btn" className="inline-flex items-center gap-2 mt-8 bg-ktc-goldLight hover:bg-ktc-gold text-ktc-greenDark font-semibold px-7 py-3.5 rounded-md transition-colors">
                  Enter the harvest <ArrowRight size={18} />
                </button>
              )}
            </div>
          </div>
        ))}
      </section>
    );
  }

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
  const totalH = isMobile ? '440vh' : '520vh';
  const motifRot = seg(progress, [[0, -6], [1, 30]]);
  const motifYPct = seg(progress, [[0, 4], [1, -14]]);

  return (
    <section ref={ref} data-testid="cinematic-intro" style={{ height: totalH }} className="relative">
      <div className="sticky top-0 h-screen w-full overflow-hidden z-[60] bg-[#0F3320] text-ktc-ivory">
        {scenes.map((s, i) => {
          const opacity = seg(progress, OP[i]);
          const yOff = seg(progress, YY[i]);
          return (
            <div key={i} className="absolute inset-0" style={{ opacity, pointerEvents: opacity > 0.5 ? 'auto' : 'none' }}>
              <div className="absolute inset-0 bg-[#0A1E13]" />
              {s.image && (
                <img src={s.image} alt="" aria-hidden loading={i === 0 ? 'eager' : 'lazy'} className="absolute inset-0 w-full h-full object-cover opacity-[0.92]" style={{ transform: 'scale(1.06)' }} />
              )}
              <div className="absolute inset-0" style={{ background: OVERLAY }} />
              <div className="absolute inset-0" style={{ background: s.glow }} />
              <div className="absolute inset-0 grain-texture opacity-[0.12]" aria-hidden />
              <GrainDots className="absolute inset-0 pointer-events-none" />
              <div className="absolute right-[-40px] sm:right-[6%] top-1/2 text-ktc-goldLight/25 pointer-events-none" style={{ transform: `translateY(calc(-50% + ${motifYPct}%)) rotate(${motifRot}deg)` }}>
                <RiceStalk className="h-[62vh] w-auto" />
              </div>
              <div className="absolute left-[-60px] sm:left-[3%] bottom-[-4%] text-ktc-goldLight/10 pointer-events-none hidden sm:block" style={{ transform: `translateY(${motifYPct}%) rotate(${-motifRot}deg) scaleX(-1)` }}>
                <RiceStalk className="h-[42vh] w-auto" />
              </div>

              <div className="relative h-full flex flex-col items-center justify-center text-center px-6" style={{ transform: `translateY(${yOff}px)` }}>
                <span className="text-[11px] sm:text-[12px] uppercase tracking-[0.3em] text-ktc-goldLight intro-legible">{s.kicker}</span>
                <h2 className="font-intro intro-legible text-3xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] mt-4 max-w-4xl text-balance">{s.title}</h2>
                <p className="text-ktc-ivory/80 intro-legible mt-5 text-base sm:text-lg max-w-xl">{s.sub}</p>

                {s.stats && (
                  <div className="flex justify-center gap-8 sm:gap-14 mt-8">
                    {s.stats.map((st) => (
                      <div key={st.l} className="flex flex-col items-center">
                        <div className="font-intro intro-legible text-3xl sm:text-4xl text-ktc-goldLight">{st.n}</div>
                        <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-ktc-ivory/70 mt-1">{st.l}</div>
                      </div>
                    ))}
                  </div>
                )}

                {s.cta && (
                  <button type="button" onClick={enter} data-testid="intro-enter-btn" className="inline-flex items-center gap-2 mt-9 bg-ktc-goldLight hover:bg-ktc-gold text-ktc-greenDark font-semibold px-8 py-4 rounded-md transition-colors">
                    Enter the harvest <ArrowRight size={18} />
                  </button>
                )}

                {i === 0 && (
                  <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-ktc-ivory/45 text-[10px] uppercase tracking-[0.24em]">
                    Scroll <ChevronDown size={16} className="animate-bounce" />
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* progress dots */}
        <div className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-[62] flex flex-col gap-3">
          {scenes.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to scene ${i + 1}`}
              onClick={() => goTo(i)}
              data-testid={`intro-dot-${i}`}
              className={`rounded-full transition-all duration-300 ${i === active ? 'h-6 w-1.5 bg-ktc-goldLight' : 'h-1.5 w-1.5 bg-ktc-ivory/35 hover:bg-ktc-ivory/70'}`}
            />
          ))}
        </div>

        {/* skip */}
        <button
          type="button"
          onClick={enter}
          data-testid="intro-skip"
          className="absolute top-6 right-6 z-[62] text-[11px] uppercase tracking-[0.2em] text-ktc-ivory/75 border border-ktc-ivory/20 rounded-full px-4 py-2 hover:text-ktc-ivory hover:border-ktc-ivory/50 transition-colors backdrop-blur-sm"
        >
          Skip intro →
        </button>
      </div>
    </section>
  );
};

export default CinematicIntro;
